/**
 * Custom hook for message processing and status management
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { parseDate } from '../utils/date';
import { log } from '../utils/logger';
import {
  isLayoutDebugEnabled,
  noteLayoutDebugEvent,
  noteServerEvents,
} from '../utils/layoutDebug';
import { extractStreamingMessage } from '../types/parlant';
import type { ParlantEvent } from '../types/parlant';
import {
  applyInlineStatusToGroupedTurns,
  groupAgentTurns,
  isAgentSource,
} from '../utils/agentTurnGrouping';

// Debug logging helper - only logs when debug mode is enabled
let debugModeEnabled = false;
const debugLog = (...args: unknown[]) => {
  if (debugModeEnabled || isLayoutDebugEnabled()) {
    log('[MessageProcessing]', ...args);
  }
};

/**
 * Interface for message with status
 */
export interface MessageInterface {
  id?: string;
  kind: string;
  source: string;
  creationUtc: Date;
  offset: number;
  correlationId: string;
  data: Record<string, unknown>;
  status: string | null;
  error?: string;
  isStatusMessage?: boolean;
  serverStatus?: string;
  created_at?: string;
  /** Whether this message has been flagged by moderation */
  flagged?: boolean;
  /** Flagging reason or comment */
  flaggedReason?: string;
  /** Draft content for agent messages */
  draft?: string;
  /** Whether this event was deleted */
  deleted?: boolean;
  /** Inline status shown inside the active agent bubble (replaces standalone status pill) */
  displayStatusText?: string;
  /** Stable display key for grouped agent turns */
  turnKey?: string;
}

/**
 * Interface for status event data
 */
interface StatusEventData {
  status?: string;
  exception?: string;
  data?: {
    stage?: string;
  };
}

function mergeEvents(prev: ParlantEvent[], incoming: ParlantEvent[]): ParlantEvent[] {
  const eventMap = new Map<string, ParlantEvent>();

  prev.forEach(event => {
    if (event.id) eventMap.set(event.id, event);
  });

  incoming.forEach(event => {
    if (event.id) eventMap.set(event.id, event);
  });

  return Array.from(eventMap.values()).sort((a, b) => a.offset - b.offset);
}

function eventHasVisibleAgentContent(event: ParlantEvent): boolean {
  if (event.kind !== 'message' || event.deleted) return false;
  if (!isAgentSource(event.source)) return false;

  const text = extractStreamingMessage(event.data as Record<string, unknown>);
  return typeof text === 'string' && text.trim().length > 0;
}

function messageDataSnapshot(data: Record<string, unknown> | undefined): string {
  if (!data) return '';
  return JSON.stringify({
    message: extractStreamingMessage(data),
    status: data.status,
    chunks: data.chunks,
    draft: data.draft,
    flagged: data.flagged,
    _interrupted: data._interrupted,
  });
}

function messagesStructurallyEqual(
  prev: MessageInterface,
  next: MessageInterface
): boolean {
  return (
    prev.id === next.id &&
    prev.offset === next.offset &&
    prev.source === next.source &&
    prev.status === next.status &&
    prev.serverStatus === next.serverStatus &&
    prev.flagged === next.flagged &&
    prev.flaggedReason === next.flaggedReason &&
    prev.draft === next.draft &&
    prev.deleted === next.deleted &&
    prev.error === next.error &&
    messageDataSnapshot(prev.data) === messageDataSnapshot(next.data)
  );
}

/**
 * Interface for pending message
 */
export interface PendingMessage {
  id?: string;
  kind: string;
  source: string;
  creationUtc: Date;
  serverStatus: string;
  offset: number;
  correlationId: string;
  data: { message: string };
  status: null;
}

/**
 * Message processing hook options
 */
interface UseMessageProcessingOptions {
  t: (key: string, params?: Record<string, string | number> & { defaultValue?: string }) => string;
  /** External messages state (for state persistence across mode switches) */
  externalMessages?: MessageInterface[];
  /** External setter for messages */
  externalSetMessages?: React.Dispatch<React.SetStateAction<MessageInterface[]>>;
  /** External status message state */
  externalStatusMessage?: MessageInterface | null;
  /** External setter for status message */
  externalSetStatusMessage?: React.Dispatch<React.SetStateAction<MessageInterface | null>>;
  /** External pending message state */
  externalPendingMessage?: PendingMessage;
  /** External setter for pending message */
  externalSetPendingMessage?: React.Dispatch<React.SetStateAction<PendingMessage>>;
  /** External events state */
  externalEvents?: ParlantEvent[];
  /** External setter for events */
  externalSetEvents?: React.Dispatch<React.SetStateAction<ParlantEvent[]>>;
  /** Enable detailed debug logging */
  debugMode?: boolean;
}

/**
 * Message processing hook return value
 */
interface UseMessageProcessingReturn {
  messages: MessageInterface[];
  statusMessage: MessageInterface | null;
  pendingMessage: PendingMessage;
  setPendingMessage: (message: Partial<PendingMessage>) => void;
  clearPendingMessage: () => void;
  processEvents: (events: ParlantEvent[]) => void;
  addPendingMessage: (content: string) => void;
  injectMessages: (messages: MessageInterface[]) => void;
  /**
   * O(1) in-place update for streaming chunk patches.
   * Only updates the `data` of the matching event; does NOT trigger a full
   * re-sort.  Call this from the transport layer when a per-message SSE
   * frame arrives with new chunks for an event that is already in the list.
   */
  patchStreamingEvent: (eventId: string, partialData: Record<string, unknown>) => void;
}

/**
 * Custom hook for processing messages and managing status
 * @param options - Hook configuration
 * @returns Message processing functions and state
 */
export const useMessageProcessing = ({
  t,
  externalMessages,
  externalSetMessages,
  externalStatusMessage,
  externalSetStatusMessage,
  externalPendingMessage,
  externalSetPendingMessage,
  externalEvents,
  externalSetEvents,
  debugMode = false,
}: UseMessageProcessingOptions): UseMessageProcessingReturn => {
  // Enable debug logging based on debugMode prop
  debugModeEnabled = debugMode;
  
  // Default pending message state
  const defaultPendingMessage: PendingMessage = {
    kind: 'message',
    source: 'customer',
    creationUtc: new Date(),
    serverStatus: 'pending',
    offset: 0,
    correlationId: '',
    data: { message: '' },
    status: null,
  };

  // Use external state if provided, otherwise use local state
  const [localStatusMessage, setLocalStatusMessage] = useState<MessageInterface | null>(null);
  const [inlineStatus, setInlineStatus] = useState<{ correlationId: string; text: string } | null>(
    null
  );
  const [localEvents, setLocalEvents] = useState<ParlantEvent[]>([]);
  const [localPendingMessage, setLocalPendingMessage] = useState<PendingMessage>(defaultPendingMessage);
  const [injectedMessages, setInjectedMessages] = useState<MessageInterface[]>([]);

  // Determine which state to use - external (shared) or local
  const hasExternalState = externalMessages !== undefined && externalSetMessages !== undefined;
  
  // Note: We use setMessages for persistence but finalMessages (computed) for rendering
  // Only needed when external state is provided
  const setMessages = externalSetMessages;
  const statusMessage = hasExternalState && externalStatusMessage !== undefined ? externalStatusMessage : localStatusMessage;
  const setStatusMessage = hasExternalState && externalSetStatusMessage ? externalSetStatusMessage : setLocalStatusMessage;
  const events = hasExternalState && externalEvents !== undefined ? externalEvents : localEvents;
  const setEvents = hasExternalState && externalSetEvents ? externalSetEvents : setLocalEvents;
  const pendingMessageState = hasExternalState && externalPendingMessage !== undefined ? externalPendingMessage : localPendingMessage;
  const setPendingMessageState = hasExternalState && externalSetPendingMessage ? externalSetPendingMessage : setLocalPendingMessage;

  // Filter message events, excluding deleted events
  const messageEvents = useMemo(
    () => events?.filter((e) => e.kind === 'message' && !e.deleted) || [],
    [events]
  );

  // Get all status events sorted by offset
  const statusEvents = useMemo(
    () => events?.filter((e) => e.kind === 'status').sort((a, b) => a.offset - b.offset) || [],
    [events]
  );

  // Add status, flagged, and draft to messages
  const withStatusMessages = useMemo(() => {
    debugLog('🔄 Computing message statuses for', messageEvents.length, 'messages');
    // #region agent log
    debugLog('[useMessageProcessing.ts:withStatusMessages] Computing statuses', {
      messageCount: messageEvents.length,
      statusEventsCount: statusEvents.length,
      timestamp: Date.now(),
      sessionId: 'debug-session',
      hypothesisId: 'H1'
    });
    // #endregion
    return messageEvents.map((newMessage, i) => {
      const eventData = newMessage.data as Record<string, unknown>;
      const messageData: MessageInterface = {
        id: newMessage.id,
        kind: newMessage.kind,
        source: newMessage.source,
        creationUtc: parseDate(newMessage.creation_utc),
        offset: newMessage.offset,
        correlationId: newMessage.correlation_id || '',
        data: eventData,
        status: '',
        // Extract flagged status from event data
        flagged: eventData?.flagged === true,
        flaggedReason: typeof eventData?.flagged === 'string' ? eventData.flagged : undefined,
        // Extract draft from event data (for agent messages)
        draft: typeof eventData?.draft === 'string' ? eventData.draft : undefined,
        deleted: newMessage.deleted,
      };
      
      // Find the next message's offset (or Infinity if this is the last message)
      const nextMessageOffset = messageEvents[i + 1]?.offset ?? Infinity;
      const hasNextMessage = nextMessageOffset !== Infinity;
      
      // Find status events that come AFTER this message
      const statusEventsAfterMessage = statusEvents.filter(
        se => se.offset > newMessage.offset
      );
      
      // Key insight: Once we see a "ready" status, this message is complete.
      // Any "processing" or "typing" status AFTER "ready" is for the NEXT response.
      // So we look for the FIRST "ready" status after this message, and that determines
      // if this message is complete.
      const firstReadyStatus = statusEventsAfterMessage.find(
        se => (se.data as StatusEventData)?.status === 'ready'
      );
      
      // Also check if there's any "processing" or "typing" status that comes BEFORE "ready"
      // These are the active statuses while this message is being generated
      const activeStatusBeforeReady = firstReadyStatus
        ? statusEventsAfterMessage.find(
            se => se.offset < firstReadyStatus.offset && 
                  ['processing', 'typing'].includes((se.data as StatusEventData)?.status || '')
          )
        : statusEventsAfterMessage.find(
            se => ['processing', 'typing'].includes((se.data as StatusEventData)?.status || '')
          );
      
      // Determine status:
      // 1. If we found a "ready" status after this message, it's ready
      // 2. If there's a next message, this one is implicitly "ready"
      // 3. If there's an active processing/typing status (and no ready yet), use that
      // 4. Otherwise, status is null
      if (firstReadyStatus) {
        messageData.status = 'ready';
      } else if (hasNextMessage) {
        messageData.status = 'ready';
      } else if (activeStatusBeforeReady) {
        const statusData = activeStatusBeforeReady.data as StatusEventData;
        messageData.status = statusData.status || null;
      } else {
        messageData.status = null;
      }
      
      // Debug log for each message's computed status
      const msgText = typeof eventData?.message === 'string' 
        ? eventData.message.substring(0, 30) + '...'
        : '[non-string]';
      debugLog(`  msg[${i}] source=${newMessage.source}, status=${messageData.status}, hasNext=${hasNextMessage}, statusEventsAfter=${statusEventsAfterMessage.length}, hasReady=${!!firstReadyStatus}, text="${msgText}"`);
      
      // #region agent log
      // Intentionally avoid per-message debug logs to reduce noise
      // #endregion
      
      // Handle error status - find the error status event if any
      if (messageData.status === 'error' || statusEventsAfterMessage.some(se => (se.data as StatusEventData)?.status === 'error')) {
        const errorStatus = statusEventsAfterMessage.find(se => (se.data as StatusEventData)?.status === 'error');
        if (errorStatus) {
          const errorData = errorStatus.data as StatusEventData;
          messageData.status = 'error';
          messageData.error = errorData?.exception;
          messageData.serverStatus = 'error';
        }
      }
      return messageData;
    });
  }, [messageEvents, statusEvents]);

  // Track if we should clear pending message
  const [shouldClearPending, setShouldClearPending] = useState(false);
  const previousMessagesRef = useRef<Map<string, MessageInterface>>(new Map());

  // Compute final messages array synchronously (no useEffect delay!)
  // This prevents flash when withStatusMessages updates but messages state hasn't caught up
  const finalMessages = useMemo(() => {
    // Merge withStatusMessages and injectedMessages, deduplicating by ID
    const messageMap = new Map<string, MessageInterface>();
    
    withStatusMessages.forEach(msg => {
      if (msg.id) messageMap.set(msg.id, msg);
    });
    
    injectedMessages.forEach(msg => {
      if (msg.id) messageMap.set(msg.id, msg);
    });
    
    const newMessages = Array.from(messageMap.values()).sort((a, b) => a.offset - b.offset);
    
    // ANTI-FLASH FIX: If computed messages are empty but externalMessages has data,
    // use externalMessages as fallback. This prevents flash during mode switches
    // where events might be temporarily empty but the persisted messages exist.
    if (newMessages.length === 0 && externalMessages && externalMessages.length > 0) {
      debugLog('🛡️ Preventing message flash - using external messages as fallback');
      return externalMessages;
    }

    const stabilized = newMessages.map(msg => {
      if (!msg.id) return msg;
      const previous = previousMessagesRef.current.get(msg.id);
      if (previous && messagesStructurallyEqual(previous, msg)) {
        return previous;
      }
      return msg;
    });

    previousMessagesRef.current = new Map(
      stabilized.filter(msg => msg.id).map(msg => [msg.id!, msg])
    );

    const grouped = groupAgentTurns(stabilized);
    return applyInlineStatusToGroupedTurns(grouped, inlineStatus);
  }, [withStatusMessages, injectedMessages, externalMessages, inlineStatus]);

  // Update external messages state for persistence (avoid redundant updates)
  useEffect(() => {
    if (!hasExternalState || !setMessages) return;

    // Skip if external messages already match (prevents update loops)
    if (externalMessages && externalMessages.length === finalMessages.length) {
      let isSame = true;
      for (let i = 0; i < finalMessages.length; i++) {
        const nextMsg = finalMessages[i];
        const prevMsg = externalMessages[i];
        if (
          nextMsg.id !== prevMsg.id ||
          nextMsg.offset !== prevMsg.offset ||
          nextMsg.status !== prevMsg.status ||
          messageDataSnapshot(nextMsg.data) !== messageDataSnapshot(prevMsg.data)
        ) {
          isSame = false;
          break;
        }
      }
      if (isSame) return;
    }

    setMessages(finalMessages);
  }, [finalMessages, hasExternalState, setMessages, externalMessages]);

  // Check for pending message match and clear it
  useEffect(() => {
    const pendingMessageText = pendingMessageState?.data?.message;
    if (pendingMessageText) {
      const matchingServerMessage = finalMessages.find(msg =>
        msg.id &&
        (msg.source === 'customer' || msg.source === 'customer_ui') &&
        (msg.data as Record<string, unknown>)?.message === pendingMessageText
      );
      if (matchingServerMessage) {
        setShouldClearPending(true);
      }
    }
  }, [finalMessages, pendingMessageState]);

  /**
   * Sets pending message with partial updates
   */
  const setPendingMessage = useCallback((message: Partial<PendingMessage>): void => {
    setPendingMessageState(prev => ({ ...prev, ...message }));
  }, []);

  /**
   * Clears the pending message
   */
  const clearPendingMessage = useCallback((): void => {
    setPendingMessageState({
      kind: 'message',
      source: 'customer',
      creationUtc: new Date(),
      serverStatus: 'pending',
      offset: 0,
      correlationId: '',
      data: { message: '' },
      status: null,
    });
  }, []);

  // Clear pending message when triggered
  useEffect(() => {
    if (shouldClearPending) {
      clearPendingMessage();
      setShouldClearPending(false);
    }
  }, [shouldClearPending, clearPendingMessage]);

  /**
   * Adds a pending message with content
   */
  const addPendingMessage = useCallback((content: string): void => {
    setPendingMessage({
      kind: 'message',
      source: 'customer',
      creationUtc: new Date(),
      serverStatus: 'pending',
      offset: Date.now(),
      correlationId: `temp-${Date.now()}`,
      data: { message: content },
      status: null,
    });
  }, [setPendingMessage]);

  /**
   * Injects messages directly into the message list (for bot macros, etc.)
   * 
   * @param newMessages - Messages to inject
   */
  const injectMessages = useCallback((newMessages: MessageInterface[]): void => {
    if (!newMessages || newMessages.length === 0) return;

    setInjectedMessages(prevMessages => {
      const messageMap = new Map<string, MessageInterface>();

      // Add existing injected messages
      prevMessages.forEach(msg => {
        if (msg.id) messageMap.set(msg.id, msg);
      });

      // Add new messages
      newMessages.forEach(msg => {
        if (msg.id) messageMap.set(msg.id, msg);
      });

      // Convert back to array, sorted by offset
      return Array.from(messageMap.values()).sort((a, b) => a.offset - b.offset);
    });
  }, []);

  /**
   * Processes incoming events and updates messages/status
   */
  const processEvents = useCallback((newEvents: ParlantEvent[]): void => {
    if (!newEvents || newEvents.length === 0) return;

    if (isLayoutDebugEnabled()) {
      noteServerEvents('processor', newEvents);
    }

    // #region agent log
    const statusEvents = newEvents.filter(e => e.kind === 'status');
    const messageEventsNew = newEvents.filter(e => e.kind === 'message');
    debugLog('[useMessageProcessing.ts:processEvents] Processing new events batch', {
      totalEvents: newEvents.length,
      statusEventsCount: statusEvents.length,
      messageEventsCount: messageEventsNew.length,
      statusTypes: statusEvents.map(e => (e.data as Record<string, unknown>)?.status),
      offsets: newEvents.map(e => e.offset),
      timestamp: Date.now(),
      sessionId: 'debug-session',
      hypothesisId: 'H1,H3'
    });
    // #endregion

    // Update events with deduplication by ID
    let mergedEvents: ParlantEvent[] = [];
    setEvents(prevEvents => {
      mergedEvents = mergeEvents(prevEvents, newEvents);
      return mergedEvents;
    });

    const lastStatusEvent = newEvents.slice().reverse().find((e: ParlantEvent) => e.kind === 'status');
    const statusCorrelationId = lastStatusEvent?.correlation_id || '';

    const visibleCorrelationsInBatch = messageEventsNew
      .filter(eventHasVisibleAgentContent)
      .map(event => event.correlation_id)
      .filter((correlationId): correlationId is string => Boolean(correlationId));

    // Handle status messages
    const lastStatusEventStatus = (lastStatusEvent?.data as StatusEventData)?.status;
    debugLog('📊 Last status event status:', lastStatusEventStatus || 'none');

    // Create or update status message bubble
    let statusText = '';
    if (lastStatusEventStatus === 'processing') {
      const statusEventData = lastStatusEvent?.data as StatusEventData & { data?: { stage?: string } };
      const stage = statusEventData?.data?.stage;
      if (stage) {
        // Convert stage to camelCase for translation key lookup
        // "Fetching data" → "fetchingData"
        const stageKey = stage
          .toLowerCase()
          .replace('...', '')
          .trim()
          .replace(/\s+(.)/g, (_, char) => char.toUpperCase());
        
        // Try to get translation, with explicit fallback
        const translationKey = `stage.${stageKey}`;
        const translatedStage = t(translationKey);
        
        // If translation returns the key itself, use the original stage with ellipsis
        if (translatedStage === translationKey || translatedStage.startsWith('stage.')) {
          statusText = `${stage}${stage.endsWith('...') ? '' : '...'}`;
        } else {
          statusText = translatedStage;
        }
      } else {
        statusText = t('status.thinking');
      }
    } else if (lastStatusEventStatus === 'typing') {
      statusText = t('status.typing');
    }

    // Status always renders inline on the active turn bubble (or a placeholder
    // shell before the first agent event). Standalone status is only used when
    // the server omits a correlation id.
    if (statusText) {
      if (statusCorrelationId) {
        debugLog('💬 Setting inline status on agent turn:', statusText);
        if (isLayoutDebugEnabled()) {
          noteLayoutDebugEvent('processor-status', {
            action: 'set-inline',
            statusText,
            correlationId: statusCorrelationId,
            lastStatusEventStatus,
          });
        }
        setInlineStatus({ correlationId: statusCorrelationId, text: statusText });
        setStatusMessage(null);
      } else {
        debugLog('💬 Setting standalone status message:', statusText);
        if (isLayoutDebugEnabled()) {
          noteLayoutDebugEvent('processor-status', {
            action: 'set',
            statusText,
            lastStatusEventStatus,
          });
        }
        setInlineStatus(null);
        setStatusMessage({
          id: 'status-bubble',
          kind: 'message',
          source: 'ai_agent',
          creationUtc: new Date(),
          offset: -1,
          correlationId: 'status',
          data: { message: statusText },
          status: 'typing',
          isStatusMessage: true,
        } as MessageInterface & { isStatusMessage: boolean });
      }
    } else if (lastStatusEventStatus === 'ready') {
      debugLog('✅ Clearing status feedback', { reason: 'ready' });
      if (isLayoutDebugEnabled()) {
        noteLayoutDebugEvent('processor-status', {
          action: 'clear',
          reason: 'ready',
          lastStatusEventStatus,
        });
      }
      setStatusMessage(null);
      setInlineStatus(null);
    } else if (visibleCorrelationsInBatch.length > 0) {
      debugLog('✅ Clearing standalone status feedback', {
        reason: 'visible agent content',
        correlations: visibleCorrelationsInBatch,
      });
      if (isLayoutDebugEnabled()) {
        noteLayoutDebugEvent('processor-status', {
          action: 'clear',
          reason: 'visible-agent-content',
          correlations: visibleCorrelationsInBatch,
        });
      }
      setStatusMessage(null);
      // Inline status clears only on ready so it stays steady between segments.
    }
  }, [t, setEvents]);

  /**
   * O(1) streaming patch — updates only the data of a single event.
   * Avoids the full Map→sort rebuild path for high-frequency chunk updates.
   */
  const patchStreamingEvent = useCallback((eventId: string, partialData: Record<string, unknown>): void => {
    setEvents(prev => {
      const idx = prev.findIndex(e => e.id === eventId);
      if (idx === -1) return prev;

      const existing = prev[idx];
      const patched: ParlantEvent = {
        ...existing,
        data: { ...(existing.data as Record<string, unknown>), ...partialData },
      };

      // Return a new array but only change the single slot — O(n) copy is
      // unavoidable for immutability, but we avoid the O(n log n) sort.
      const next = [...prev];
      next[idx] = patched;
      return next;
    });
  }, [setEvents]);

  return {
    messages: finalMessages,
    statusMessage,
    pendingMessage: pendingMessageState,
    setPendingMessage,
    clearPendingMessage,
    processEvents,
    addPendingMessage,
    injectMessages,
    patchStreamingEvent,
  };
};
